/**
 * ScriptApiRegistry — the runtime side of the engine-API: namespace -> capability-module
 * provider, assembled into the (generated) {@link EngineApi} surface a script's `this.engine`
 * exposes. The static SHAPE is codegen'd (`EngineApi.generated.ts`); this is the dynamic
 * assembler that the per-runtime DI fills (player/editor/headless register their own impls
 * of the same module contracts).
 *
 * Two ways to register a provider:
 *  - `register(provider)` — reads the namespace + tier off the `@scriptApi` metadata stamped
 *    on the provider's class (the ergonomic form for decorated modules).
 *  - `register(namespace, provider, tier?)` — explicit, for providers without metadata
 *    (e.g. plain-object adapters bridging a legacy capability).
 *
 * `build()` returns the full surface; `buildSafe()` returns only `safe`-tier namespaces — the
 * sandbox view handed to user scripts (§9). Withholding is by construction here and by TYPE
 * in the generated `SafeEngineApi`, never by ad-hoc filtering at each call site.
 *
 * Design ref: `.refactor/SCRIPTING-API-DESIGN.md` §2 (model) + §9 (scoping) + §13 step 3.
 */

import type { EngineApi, SafeEngineApi } from "./EngineApi.generated";
import { ENGINE_API_NAMESPACES } from "./EngineApi.generated";
import { getScriptApiMeta, type ScriptApiTier } from "./scriptApi";
import { PhysicsScriptApi, type PhysicsBacking } from "./modules/PhysicsScriptApi";
import { EventsScriptApi, type EventsBacking } from "./modules/EventsScriptApi";
import { SceneScriptApi, type SceneBacking } from "./modules/SceneScriptApi";
import { InputScriptApi, type InputBacking } from "./modules/InputScriptApi";
import { AudioScriptApi, type AudioBacking } from "./modules/AudioScriptApi";
import { VFXScriptApi, type VFXBacking } from "./modules/VFXScriptApi";
import { CameraScriptApi, type CameraBacking } from "./modules/CameraScriptApi";
import { LevelScriptApi } from "./modules/LevelScriptApi";
import { UiScriptApi, type UIBacking } from "./modules/UiScriptApi";

interface RegisteredModule {
  provider: object;
  tier: ScriptApiTier;
}

export class ScriptApiRegistry {
  private readonly _modules = new Map<string, RegisteredModule>();

  /** Register a `@scriptApi`-decorated provider (namespace + tier read from its metadata). */
  register(provider: object): this;
  /** Register a provider under an explicit namespace/tier (defaults tier to `'safe'`). */
  register(namespace: string, provider: object, tier?: ScriptApiTier): this;
  register(a: object | string, b?: object, tier?: ScriptApiTier): this {
    let namespace: string;
    let provider: object;
    let resolvedTier: ScriptApiTier;

    if (typeof a === "string") {
      if (!b) throw new Error(`[ScriptApiRegistry] no provider for namespace '${a}'`);
      namespace = a;
      provider = b;
      resolvedTier = tier ?? getScriptApiMeta(b)?.tier ?? "safe";
    } else {
      const meta = getScriptApiMeta(a);
      if (!meta) {
        throw new Error(
          "[ScriptApiRegistry] provider has no @scriptApi metadata; " +
            "use register(namespace, provider, tier?) to register it explicitly",
        );
      }
      namespace = meta.namespace;
      provider = a;
      resolvedTier = meta.tier;
    }

    // Guard the one place where the typed `EngineApi` surface and the runtime contents can
    // silently diverge: a namespace that isn't in the generated surface (typo / stale codegen)
    // would be assembled into `build()` yet typed `as EngineApi` without a matching member.
    if (!(ENGINE_API_NAMESPACES as readonly string[]).includes(namespace)) {
      console.warn(
        `[ScriptApiRegistry] namespace '${namespace}' is not in the generated EngineApi surface ` +
          `(${ENGINE_API_NAMESPACES.join(", ")}); run the script-api codegen if it's a new module`,
      );
    }
    if (this._modules.has(namespace)) {
      console.warn(`[ScriptApiRegistry] namespace '${namespace}' re-registered (overwriting)`);
    }
    this._modules.set(namespace, { provider, tier: resolvedTier });
    return this;
  }

  has(namespace: string): boolean {
    return this._modules.has(namespace);
  }

  get<T = unknown>(namespace: string): T | undefined {
    return this._modules.get(namespace)?.provider as T | undefined;
  }

  get size(): number {
    return this._modules.size;
  }

  /** Assemble the full {@link EngineApi} — every registered namespace. */
  build(): EngineApi {
    const api: Record<string, object> = {};
    for (const [namespace, mod] of this._modules) api[namespace] = mod.provider;
    return api as EngineApi;
  }

  /** Assemble the sandbox {@link SafeEngineApi} — only `safe`-tier namespaces (§9). */
  buildSafe(): SafeEngineApi {
    const api: Record<string, object> = {};
    for (const [namespace, mod] of this._modules) {
      if (mod.tier === "safe") api[namespace] = mod.provider;
    }
    return api as SafeEngineApi;
  }

  clear(): void {
    this._modules.clear();
  }
}

/** The standard capability backings a runtime injects (DI per runtime). */
export interface EngineApiBackings {
  physics?: PhysicsBacking;
  events?: EventsBacking;
  scene?: SceneBacking;
  input?: InputBacking;
  audio?: AudioBacking;
  vfx?: VFXBacking;
  camera?: CameraBacking;
  ui?: UIBacking;
  // NOTE: `gpu` is deliberately NOT a session-shared backing here — engine.gpu v2 is per-script
  // scoped (auto-dispose on script destroy). Runtimes pass a GpuBacking via GameObjectContextDeps
  // instead, and GameObjectEngineContext overlays a scoped GpuScriptApi onto this surface.
  /**
   * Enable the `engine.level` namespace (level load/preload/unload/progress). No backing object —
   * it routes to the global `levelManager` (which the host wires via `setLoaderHost`). Set `true`
   * in runtimes that have a level system (player, editor); omit in headless/tests.
   */
  level?: boolean;
}

/**
 * Assemble the standard {@link EngineApi} from a runtime's capability backings — the one-call
 * DI entry point each runtime (player/editor/headless) uses to wire its `this.engine`. Only the
 * provided backings are registered; the rest stay absent (optional in the generated surface).
 */
export function buildEngineApi(backings: EngineApiBackings): EngineApi {
  const reg = new ScriptApiRegistry();
  if (backings.physics) reg.register(new PhysicsScriptApi(backings.physics));
  if (backings.events) reg.register(new EventsScriptApi(backings.events));
  if (backings.scene) reg.register(new SceneScriptApi(backings.scene));
  if (backings.input) reg.register(new InputScriptApi(backings.input));
  if (backings.audio) reg.register(new AudioScriptApi(backings.audio));
  if (backings.vfx) reg.register(new VFXScriptApi(backings.vfx));
  if (backings.camera) reg.register(new CameraScriptApi(backings.camera));
  if (backings.ui) reg.register(new UiScriptApi(backings.ui));
  if (backings.level) reg.register(new LevelScriptApi());
  return reg.build();
}
